/**
 * Canonical styling for destructive (danger) filled buttons.
 *
 * Spread this into a component's own `makeStyles` block, the same way
 * `visuallyHiddenStyle` is used:
 *
 * ```ts
 * const useStyles = makeStyles({
 *   deleteButton: destructiveButtonStyle,
 * });
 * // <Button appearance="primary" className={styles.deleteButton}>Delete</Button>
 * ```
 *
 * It exists because a destructive `<Button appearance="primary">` is deceptively
 * hard to style correctly, and every hand-rolled copy in this repo got at least
 * one of the following wrong.
 *
 * 1. **Fluent owns the interaction states.** The `primary` appearance ships its
 *    own rules for `:hover` and for `:hover:active, :active:focus-visible`.
 *    Overriding only the base `backgroundColor` leaves those intact, so the
 *    button reverts to brand blue exactly when the pointer lands on it, and
 *    again on keyboard activation. Note that `:active:focus-visible` is a
 *    separate selector from `:hover:active`: overriding only the latter leaves
 *    keyboard users on the brand color, which mouse testing never reveals.
 *
 * 2. **`colorPaletteRed*` collapses.** `colorPaletteRedForeground3` equals the
 *    rest color in the light theme and the hover color in the dark theme, so
 *    using it for the pressed state erases press feedback in both. The
 *    `colorStatusDanger*` ramp is purpose-built for this: it darkens
 *    monotonically (#c50f1f -> #b10e1c -> #960b18), is identical across themes,
 *    and gives white label text a 6.07:1 contrast ratio at rest.
 *
 * 3. **Disabled has to win.** An unscoped base `backgroundColor` outranks
 *    Fluent's disabled rule, leaving a full-strength red button wearing disabled
 *    grey text while an action is in flight. Scoping every rule away from
 *    `:disabled` and `[aria-disabled="true"]` hands both native `disabled` and
 *    `disabledFocusable` states back to Fluent. `disabledFocusable` is the
 *    correct way to disable a button inside a dialog: a plain `disabled` button
 *    drops out of the focus order and takes focus to `<body>`, defeating the
 *    dialog's focus trap.
 *
 * @module
 */
import { tokens, type GriffelStyle } from '@coreai/fluentui-react';

export const destructiveButtonStyle: GriffelStyle = {
  '&:not([aria-disabled="true"]):not(:disabled)': {
    backgroundColor: tokens.colorStatusDangerBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': {
      backgroundColor: tokens.colorStatusDangerBackground3Hover,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':hover:active': {
      backgroundColor: tokens.colorStatusDangerBackground3Pressed,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    ':active:focus-visible': {
      backgroundColor: tokens.colorStatusDangerBackground3Pressed,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    // Forced-colors mode supplies its own system palette; keep the button
    // legible there instead of forcing the danger fill through.
    '@media (forced-colors: active)': {
      backgroundColor: 'ButtonFace',
      color: 'ButtonText',
      ':hover': {
        backgroundColor: 'ButtonFace',
        color: 'ButtonText',
      },
      ':hover:active': {
        backgroundColor: 'ButtonFace',
        color: 'ButtonText',
      },
      ':active:focus-visible': {
        backgroundColor: 'ButtonFace',
        color: 'ButtonText',
      },
    },
  },
};
